/**
 * @velyn-custom BEGIN — vely-quick-access.js
 * Smooth-scroll icon nav with pin-on-scroll:
 * stays in document flow at top, pins under header while scrolling down,
 * follows --vely-header-offset when the header hides or shows,
 * returns to original place when scrolling back to the start.
 */
(() => {
  if (customElements.get('vely-quick-access')) return;

  class VelyQuickAccess extends HTMLElement {
    connectedCallback() {
      if (this._initStarted) return;
      this._initStarted = true;

      this._shell = this.closest('.section-vely-quick-access');
      this._sentinel = this._shell?.previousElementSibling?.classList?.contains('vely-qa__sentinel')
        ? this._shell.previousElementSibling
        : this._shell?.parentElement?.querySelector('.vely-qa__sentinel');
      this._pinEnabled = this.dataset.pin === 'true';
      this._pinned = false;

      this._onClick = this._onClick.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onHeaderVisibility = this._onHeaderVisibility.bind(this);

      this.addEventListener('click', this._onClick);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('vely:header-visibility', this._onHeaderVisibility);

      this._syncPinned();
      this._syncActiveFromScroll();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('vely:header-visibility', this._onHeaderVisibility);
      this._unpin(true);
      this._initStarted = false;
    }

    _headerHeight() {
      const live = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--vely-header-offset')
      );
      if (!Number.isNaN(live) && live >= 0) return live;
      return (
        parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0
      );
    }

    _barHeight() {
      return this._shell?.offsetHeight || this.offsetHeight || 0;
    }

    _headerOffset() {
      const extra = parseFloat(this.dataset.offsetExtra || '0') || 0;
      const stickyExtra = this._pinned || this._pinEnabled ? this._barHeight() : 0;
      return this._headerHeight() + stickyExtra + extra;
    }

    _resolveTarget(anchorId) {
      if (!anchorId) return null;
      const id = String(anchorId).replace(/^#/, '').trim();
      if (!id) return null;

      let el = document.getElementById(id);
      if (el) return el;

      el = document.getElementById(`shopify-section-${id}`);
      if (el) return el;

      el = document.querySelector(`[id$="__${CSS.escape(id)}"]`);
      if (el) return el;

      return document.querySelector(CSS.escape ? `#${CSS.escape(id)}` : `#${id}`);
    }

    _sentinelTop() {
      if (!this._sentinel) return 0;
      return this._sentinel.getBoundingClientRect().top + window.pageYOffset;
    }

    _pin() {
      if (!this._pinEnabled || !this._shell || this._pinned) return;
      const height = this._barHeight();
      if (this._sentinel) {
        this._sentinel.style.height = `${height}px`;
      }
      this._shell.classList.add('is-pinned');
      this._pinned = true;
      this.setAttribute('data-sticky', 'true');
    }

    _unpin(force = false) {
      if (!this._shell || (!this._pinned && !force)) return;
      this._shell.classList.remove('is-pinned');
      this._shell.style.top = '';
      if (this._sentinel) {
        this._sentinel.style.height = '';
      }
      this._pinned = false;
      this.removeAttribute('data-sticky');
    }

    _syncPinned() {
      if (!this._pinEnabled || !this._shell || !this._sentinel) return;

      const shouldPin = window.pageYOffset + this._headerHeight() >= this._sentinelTop() - 1;
      if (shouldPin) {
        this._pin();
      } else {
        this._unpin();
      }
    }

    _onHeaderVisibility() {
      this._syncPinned();
      this._syncActiveFromScroll();
    }

    _scrollToTarget(target) {
      if (!target) return;

      if (this._pinEnabled) {
        this._pin();
      }

      const offset = this._headerOffset();
      const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    _onClick(event) {
      const link = event.target.closest('[data-vely-qa-anchor]');
      if (!link || !this.contains(link)) return;

      const anchorId = link.getAttribute('data-vely-qa-anchor');
      if (!anchorId) return;

      const target = this._resolveTarget(anchorId);
      if (!target) return;

      event.preventDefault();
      this._setActive(link);
      this._scrollToTarget(target);

      if (history.replaceState) {
        history.replaceState(null, '', `#${anchorId.replace(/^#/, '')}`);
      }
    }

    _setActive(activeLink) {
      this.querySelectorAll('.vely-qa__item').forEach((item) => {
        item.classList.toggle('is-active', item === activeLink);
      });
    }

    _syncActiveFromScroll() {
      const links = Array.from(this.querySelectorAll('[data-vely-qa-anchor]'));
      if (!links.length) return;

      const offset = this._headerOffset() + 8;
      let current = null;

      for (const link of links) {
        const target = this._resolveTarget(link.getAttribute('data-vely-qa-anchor'));
        if (!target) continue;
        const top = target.getBoundingClientRect().top;
        if (top - offset <= 0) current = link;
      }

      if (current) this._setActive(current);
    }

    _onScroll() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this._syncPinned();
        this._syncActiveFromScroll();
      });
    }

    _onResize() {
      this._syncPinned();
      this._syncActiveFromScroll();
    }
  }

  customElements.define('vely-quick-access', VelyQuickAccess);
})();
/* @velyn-custom END — vely-quick-access.js */
